import { createListenerMiddleware } from '@reduxjs/toolkit';
import { locationChanged } from '../ducks/routing.duck';

// Drivelo: action types dispatched when an account has actually been created
// (email signup, and signup through an identity provider such as Google).
const SIGNUP_COMPLETED_ACTIONS = ['auth/signup/fulfilled', 'auth/signupWithIdp/fulfilled'];

// Create a Redux Toolkit listener middleware for analytics handlers. Each
// handler should have the following methods:
//
// - trackPageView(canonicalPath, previousPath): called when the URL is changed
// - trackSignUp({ method, userType }): optional; called once a new account exists
export const createAnalyticsListenerMiddleware = handlers => {
  const listenerMiddleware = createListenerMiddleware();

  listenerMiddleware.startListening({
    actionCreator: locationChanged,
    effect: (action, listenerApi) => {
      const { canonicalPath } = action.payload;
      const previousPath = listenerApi.getOriginalState()?.routing?.currentCanonicalPath;

      handlers.forEach(handler => {
        handler.trackPageView(canonicalPath, previousPath);
      });
    },
  });

  // Drivelo: count completed signups, not visits to /signup. Google Ads was
  // recording a "Sign up" conversion on every page view of the signup form.
  // Only the method and user type are sent — never the email or name.
  listenerMiddleware.startListening({
    predicate: action => SIGNUP_COMPLETED_ACTIONS.includes(action.type),
    effect: action => {
      const params = action.meta?.arg || {};
      const method =
        action.type === 'auth/signupWithIdp/fulfilled' ? params.idpId || 'idp' : 'email';
      const userType = params.publicData?.userType || null;

      handlers.forEach(handler => {
        if (typeof handler.trackSignUp === 'function') {
          handler.trackSignUp({ method, userType });
        }
      });
    },
  });

  return listenerMiddleware;
};

// Legacy export for backward compatibility
export const createMiddleware = createAnalyticsListenerMiddleware;
